<template>
  <table class="table is-hoverable is-fullwidth is-bordered is-striped">
    <thead>
      <tr>
        <th
          v-for="header in headers"
          :key="header.key"
          class="tw-font-semibold cell"
          :data-header="header.key"
          @click="sortTable(header.key)"
        >
          <span class="tw-whitespace-nowrap">
            {{ header.display }}
            <span v-if="sortKey === header.key">
              <i :class="sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'" />
            </span>
          </span>
        </th>
        <th v-if="editable" class="is-narrow" />
        <th class="is-narrow">Details</th>
      </tr>
    </thead>
    <tbody>
      <template
        v-for="(row, rowIndex) in sortedData"
      >
        <tr :key="row.id">
          <td
            v-for="header in headers"
            :key="`${row.id}-${header.key}`"
            :data-header="header.key"
            class="cell"
            :class="[
              header.key === 'importance' ? getImportanceClass(row.importance) : '',
              header.key === 'category' ? 'bold-text' : ''
            ]"
          >
            <input
              v-if="editable"
              v-model="row[header.key]"
              class="input is-small"
            >
            <template v-else>
              {{ row[header.key] }}
            </template>
          </td>
          <td v-if="editable" class="is-narrow">
            <button
              class="button is-small is-danger"
              @click="removeItem(row.id)"
            >
              <span class="icon is-small">
                <i class="fas fa-trash" />
              </span>
            </button>
          </td>
          <td class="is-narrow">
            <button
              class="button is-small"
              @click="toggleDrawer(row.id)"
            >
              <span class="icon is-small">
                <i :class="isDrawerOpen(row.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" />
              </span>
            </button>
          </td>
        </tr>
        <tr v-if="isDrawerOpen(row.id)" :key="`drawer-${row.id}`">
          <td :colspan="headers.length + (editable ? 2 : 1)">
            <div class="drawer-content">
              <strong>Meeting Context:</strong> {{ row.meeting_context }}
            </div>
          </td>
        </tr>
      </template>
    </tbody>
    <tfoot v-if="editable">
      <tr>
        <td :colspan="headers.length + 2">
          <button
            class="button is-small is-primary"
            @click="addItem"
          >
            <span class="icon is-small">
              <i class="fas fa-plus" />
            </span>
            <span>Add Item</span>
          </button>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<script>
export default {
  name: 'KeyInfoTable',
  props: {
    data: {
      type: Array,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localData: [],
      sortKey: 'importance',
      sortOrder: 'desc',
      openDrawers: [],
    };
  },
  computed: {
    headers() {
      const priorityOrder = ['category', 'data_point', 'value', 'importance'];
      if (this.localData.length > 0) {
        const allHeaders = Object.keys(this.localData[0]).filter(key => key !== 'id');
        const orderedHeaders = priorityOrder.filter(header => allHeaders.includes(header));
        const extraHeaders = allHeaders.filter(header => !priorityOrder.includes(header));
        const finalHeaders = [...orderedHeaders, ...extraHeaders];
        return finalHeaders.map(header => ({
          display: header.split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' '),
          key: header,
        }));
      }
      return [];
    },
    sortedData() {
      return this.localData.slice().sort((a, b) => {
        let result = 0;
        if (a[this.sortKey] < b[this.sortKey]) {
          result = -1;
        } else if (a[this.sortKey] > b[this.sortKey]) {
          result = 1;
        }
        return this.sortOrder === 'asc' ? result : -result;
      });
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(newData) {
        const tableData = JSON.parse(JSON.stringify(newData));
        // Add unique IDs to each row if not already present
        this.localData = tableData.map(row => {
          if (!row.id) {
            row.id = this.generateID();
          }
          return row;
        });
        console.log('KeyInfoTable.localData', this.localData);
      },
    },
    editable(newValue, oldValue) {
      if (oldValue && !newValue) {
        this.$emit('update', this.localData);
      }
    },
  },
  methods: {
    generateID() {
      return Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
    },
    addItem() {
      const newRow = {};
      this.headers.forEach(header => {
        newRow[header.key] = '';
      });
      this.localData.push(newRow);
    },
    removeItem(id) {
      this.localData = this.localData.filter(row => row.id !== id);
    },
    sortTable(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
    },
    getImportanceClass(importance) {
      if (importance === 5) {
        return 'importance-5';
      } else if (importance === 4) {
        return 'importance-4';
      } else {
        return '';
      }
    },
    toggleDrawer(id) {
      if (this.openDrawers.includes(id)) {
      this.openDrawers = this.openDrawers.filter(i => i !== id);
      } else {
      this.openDrawers.push(id);
      }
    },
    isDrawerOpen(id) {
      return this.openDrawers.includes(id);
    },
  },
};
</script>
<style scoped>
.importance-5 {
  background-color: #c3e6cb !important;
  color: #155724 !important;
}
.importance-4 {
  background-color: #d4edda !important;
  color: #155724 !important;
}
.drawer-content {
  padding: 10px;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
}

.cell[data-header="meeting_context"] {
  display: none;
}

.bold-text {
  font-weight: bold;
}
</style>
